import React from 'react';
import { theme } from '../../styles/theme';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  ...props
}) => {
  const baseStyles = {
    fontFamily: theme.typography.fontFamily.secondary,
    fontWeight: theme.typography.fontWeight.bold,
    borderRadius: '50px', // Modern pill shape
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    border: 'none',
    outline: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    textDecoration: 'none',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || loading ? 0.6 : 1,
    letterSpacing: '0.025em',
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase', // Added uppercase for stronger style
    fontSize: size === 'sm' ? '0.75rem' : size === 'lg' ? '1rem' : '0.875rem',
  };

  const variantStyles = {
    primary: {
      backgroundColor: theme.colors.primary,
      color: theme.colors.textOnDark,
      boxShadow: '0 4px 6px -1px rgba(204, 85, 0, 0.3), 0 2px 4px -1px rgba(204, 85, 0, 0.15)',
      border: '1px solid transparent',
    },
    secondary: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.primary,
      border: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.sm,
    },
    cart: {
      backgroundColor: theme.colors.cta,
      color: theme.colors.textOnDark,
      boxShadow: '0 4px 6px -1px rgba(184, 68, 0, 0.3), 0 2px 4px -1px rgba(184, 68, 0, 0.15)',
      fontWeight: theme.typography.fontWeight.bold,
      border: '1px solid transparent',
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.colors.primary,
      border: `2px solid ${theme.colors.primary}`,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.primary,
      boxShadow: 'none',
    },
    danger: {
      backgroundColor: theme.colors.error,
      color: theme.colors.textOnDark,
      boxShadow: theme.shadows.sm,
      border: '1px solid transparent',
    },
  };

  const sizeStyles = {
    sm: {
      padding: '0.4rem 1.25rem', // Wider padding for pill shape
      height: '32px',
    },
    md: {
      padding: '0.6rem 1.75rem',
      height: '44px',
    },
    lg: {
      padding: '0.75rem 2.25rem',
      height: '52px',
    },
  };

  const hoverStyles = !disabled && !loading ? {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'ghost' ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    filter: 'brightness(1.05)',
  } : {};

  const styles = {
    ...baseStyles,
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...props.style, // Allow overriding styles
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      style={styles}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.currentTarget.style, hoverStyles);
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = variantStyles[variant].boxShadow || 'none';
        }
      }}
      {...props}
    >
      {loading ? (
        <>
          <span style={{ 
            width: '16px', 
            height: '16px', 
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }} />
          Loading...
        </>
      ) : children}
    </button>
  );
};

export default Button;
